import { useEffect, useRef } from 'react';

import { useAppDispatch } from '@verantos-dev/app-state-base';
import { clearConceptGroupsConfidence } from '@verantos-dev/app-state-cohort-builder';
import {
  addResearchAssistantMessages,
  clearResearchAssistantIsProcessing,
  resetResearchAssistantMessages,
  setResearchAssistantIsReady,
  type MessageLegacy,
} from '@verantos-dev/app-state-research-assistant';
import { logError } from '@verantos-dev/core-utils';
import { type AnalyticalVariable } from '@verantos-dev/types-cohorts';
import { ResearchAssistantMessageEnumLegacy } from '@verantos-dev/types-common';

import { useResearchAssistantSocketContext } from '../../contexts/ResearchAssistantSocketContext';
import { useHandlePopulateChatHistory } from '../../hooks/useHandlePopulateChatHistory';

type HistoryMessageResponseType = {
  detail: {
    message_id?: string | null;
    langfuse_trace_id?: string | null;
    user_prompt?: string | null;
    summary?: string | null;
    created_at: string;
    model_version?: string | null;
    is_user_generated_dsl: boolean;
    cohort_action?: string | null;
    dsl?: string | null;
    omop_metadata?: {
      session_id: string;
      file_name: string;
    } | null;
    concept_group_preview?: {
      previewId: string;
      conceptGroupName: string;
      totalCount?: number;
      domainBreakdown?: Record<string, number>;
    } | null;
    feedback?: {
      rating: 'thumbs_up' | 'thumbs_down';
      comment?: string;
      updated_at?: string;
    } | null;
    // TODO: cleanup with analytical_variables_in_cohort_builder ff
    progress_data?: {
      step: string;
      message: string;
      av_progress: Array<{
        av_index: number;
        name: string;
        action: string;
        status: string;
        retry_message?: string;
        total_count?: number;
      }>;
    } | null;
  };
  sender: string;
  timestamp: string;
};

type OmopSessionMetadataEvent = {
  omop_session_info: {
    session_id: string;
    file_name: string;
    original_file_name?: string;
    total_codes?: number;
    s3_uri?: string;
    status?: string;
    mapping_summary?: {
      total: number;
      mapped: number;
      unmapped: number;
      notFound: number;
      alreadyStandardCount: number;
    };
  };
};

type AnalyticalVariableEvent = {
  analyticalVariable: AnalyticalVariable;
  timestamp: string;
  sender: string;
};

export const useChatWindowListeners = () => {
  const dispatch = useAppDispatch();
  const { socket: currentSocket } = useResearchAssistantSocketContext();
  const { populateMessages } = useHandlePopulateChatHistory();
  const pendingOmopMetadataRef = useRef<OmopSessionMetadataEvent['omop_session_info'] | null>(null);

  useEffect(() => {
    const clearMessagesHandler = () => {
      // UI should remain disabled until welcome/history arrives
      dispatch(setResearchAssistantIsReady(false));
      dispatch(resetResearchAssistantMessages());
      dispatch(clearResearchAssistantIsProcessing({}));
      dispatch(clearConceptGroupsConfidence());
    };

    const messagesHistoryHandler = (data: HistoryMessageResponseType) => {
      if (data.detail && Array.isArray(data.detail)) {
        // CRITICAL: Clear existing messages before populating from backend
        // The backend is the source of truth for message history
        dispatch(resetResearchAssistantMessages());
        populateMessages(
          data.detail.map((message) => ({
            messageId: message.message_id || undefined,
            langfuseTraceId: message.langfuse_trace_id || undefined,
            userPrompt: message.user_prompt,
            summary: message.summary,
            createdAt: message.created_at,
            modelVersion: message.model_version,
            isUserGeneratedDsl: message.is_user_generated_dsl,
            cohortAction: message.cohort_action,
            dsl: message.dsl,
            omopMetadata: message.omop_metadata || undefined,
            conceptGroupPreview: message.concept_group_preview || undefined,
            feedback: message.feedback || undefined,
            progressData: message.progress_data || undefined,
          }))
        );
      }
      dispatch(setResearchAssistantIsReady(true));
      // NOTE: Do NOT dispatch clearResearchAssistantIsProcessing here.
      // If a workflow is in-flight, the backend will emit
      // workflow_progress_restore immediately after history, which re-sets
      // the processing state.  Clearing it here would cause a brief flash
      // of the unlocked UI before the restore event arrives.
      // Processing state is cleared by workflow_completed or the timeout guard.
    };

    const messageHandler = (message: {
      detail: string | React.ReactNode;
      timestamp: string;
      sender: string;
      message_id?: string;
      langfuse_trace_id?: string | null;
    }) => {
      if (message.detail) {
        const messageObj: MessageLegacy = {
          message: message.detail,
          timestamp: new Date(message.timestamp).toLocaleTimeString(),
          currentUser: message.sender,
          isCurrentUser: message.sender === currentSocket?.id,
          messageId: message.message_id,
          langfuseTraceId: message.langfuse_trace_id || null,
        };

        const pendingOmopMetadata = pendingOmopMetadataRef.current;
        const shouldAttachOmopMetadata =
          !!pendingOmopMetadata &&
          message.sender === 'research_assistant' &&
          typeof message.detail === 'string' &&
          message.detail.trim().length > 0;

        if (shouldAttachOmopMetadata) {
          messageObj.type = ResearchAssistantMessageEnumLegacy.OMOP_MAPPING_RESULTS;
          messageObj.omopMetadata = pendingOmopMetadata;
          pendingOmopMetadataRef.current = null;
        }

        dispatch(addResearchAssistantMessages([messageObj]));
      }
      dispatch(setResearchAssistantIsReady(true));
      dispatch(clearResearchAssistantIsProcessing({}));
    };

    const handleError = (message: { detail: string; sender: string }) => {
      logError({ message: message.detail });
      dispatch(
        addResearchAssistantMessages([
          {
            message: message.detail,
            timestamp: new Date().toLocaleTimeString(),
            currentUser: message.sender,
            isCurrentUser: false,
          },
        ])
      );
      dispatch(setResearchAssistantIsReady(true));
      dispatch(clearResearchAssistantIsProcessing({}));
    };

    const omopSessionMetadataHandler = (data: OmopSessionMetadataEvent) => {
      try {
        const omopInfo = data.omop_session_info;
        if (!omopInfo) {
          console.warn('Invalid OMOP session metadata event: missing omop_session_info');
          return;
        }

        const { session_id, file_name } = omopInfo;

        // Validate required fields
        if (!session_id || !file_name) {
          console.warn(
            'Invalid OMOP session metadata event: missing required fields (session_id or file_name)'
          );
          return;
        }

        pendingOmopMetadataRef.current = omopInfo;
      } catch (error) {
        // Handle event parsing errors
        logError({
          message: 'Failed to process OMOP session metadata event',
          error: String(error),
        });
        console.error('Error processing OMOP session metadata event:', error);
      }
    };

    const analyticalVariableHandler = (
      data:
        | AnalyticalVariableEvent
        | { analytical_variable: AnalyticalVariable; timestamp: string; sender: string }
    ) => {
      try {
        // Transform snake_case from backend to camelCase for UI consistency
        const analyticalVariable =
          'analyticalVariable' in data ? data.analyticalVariable : data.analytical_variable;
        const { timestamp, sender } = data;

        // Runtime shape guard — validate the socket event matches AnalyticalVariable
        if (
          typeof analyticalVariable !== 'object' ||
          analyticalVariable === null ||
          typeof analyticalVariable.id !== 'string' ||
          typeof analyticalVariable.name !== 'string'
        ) {
          logError({ message: 'Unexpected analytical variable shape from socket' });
          return;
        }

        const AnalyticalVariable: AnalyticalVariable = {
          ...analyticalVariable,
        };

        const messageObj: MessageLegacy = {
          message: `I've created the analytical variable '${analyticalVariable.name}'. Click to view details.`,
          timestamp: new Date(timestamp).toLocaleTimeString(),
          currentUser: sender,
          isCurrentUser: false,
          type: ResearchAssistantMessageEnumLegacy.ANALYTICAL_VARIABLE,
          analyticalVariable: AnalyticalVariable,
        };

        dispatch(addResearchAssistantMessages([messageObj]));
        dispatch(setResearchAssistantIsReady(true));
        dispatch(clearResearchAssistantIsProcessing({}));
      } catch (error) {
        logError({
          message: 'Failed to process analytical variable event',
          error: String(error),
        });
        console.error('Error processing analytical variable event:', error);
      }
    };

    if (currentSocket) {
      // Listen for 'clear' events from the server
      currentSocket.on('clear', clearMessagesHandler);
      // Listen for 'messageHistory' events from the server
      currentSocket.on('history', messagesHistoryHandler);
      // Listen for 'message' events from the server
      currentSocket.on('message', messageHandler);
      // Listen for 'error' events from the server
      currentSocket.on('error', handleError);
      // Listen for 'omop_session_metadata' events for code mapping results
      currentSocket.on('omop_session_metadata', omopSessionMetadataHandler);
      // Listen for 'analytical_variable_created' events for analytical variables
      currentSocket.on('analytical_variable_created', analyticalVariableHandler);
    }

    return () => {
      if (currentSocket) {
        currentSocket.off('clear', clearMessagesHandler);
        currentSocket.off('history', messagesHistoryHandler);
        currentSocket.off('message', messageHandler);
        currentSocket.off('error', handleError);
        currentSocket.off('omop_session_metadata', omopSessionMetadataHandler);
        currentSocket.off('analytical_variable_created', analyticalVariableHandler);
      }
    };
  }, [dispatch, currentSocket, currentSocket?.id, populateMessages]);
};
